import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalDiningOutlinedIcon from "@mui/icons-material/LocalDiningOutlined";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { setLanguage } from "../../../components/redux/appSlice";
import VLogo from "../../../components/design/VLogo";
import Seo from "../../../components/misc/Seo";
import ContactEnquiry from "../../contact/ContactEnquiry";
import FarmProcessedProductTrace from "./FarmProcessedProductTrace";

const methodLabels = {
  freeze_dried: ["Frystorkad", "Freeze-dried"],
  dehydrated: ["Torkad", "Dehydrated"],
};

function SectionTitle({ title }) {
  return <div className="ProductSectionHeading"><h2>{title}</h2></div>;
}

function ProductText({ text }) {
  const separator = text.indexOf(":");
  return separator > 0 && separator < 65
    ? <><h3>{text.slice(0, separator)}</h3><p>{text.slice(separator + 1).trim()}</p></>
    : <p>{text}</p>;
}

export default function FarmProcessedProductDetails({ product, language }) {
  const sv = language === 0;
  const dispatch = useDispatch();
  const text = (value) => value?.[language] || value?.[language === 0 ? 1 : 0] || "";
  const items = (value) => (value || []).map(text).filter(Boolean);
  const name = text(product.name);
  const method = (methodLabels[product.method] || methodLabels.freeze_dried)[language];
  const descriptions = items(product.description);
  const kitchen = items(product.kitchen);
  const nutrients = items(product.nutrientList);
  const allergens = items(product.allergens);
  const nutrition = product.nutritionDeclaration || [];
  const ingredients = text(product.ingredients);
  const shelfLife = text(product.shelfLife);
  const storage = text(product.storeData);
  const profiles = [
    { label: sv ? "Smak" : "Flavour", value: text(product.flavor) },
    { label: sv ? "Färg" : "Colour", value: text(product.color) },
    { label: sv ? "Textur" : "Texture", value: text(product.texture) },
  ].filter((item) => item.value);
  const [failedImages, setFailedImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = (product.images?.[language] || []).filter((url) => !failedImages.includes(url));
  const heroPhoto = photos[photoIndex] || photos[0];
  const markFailed = (url) => setFailedImages((current) => current.includes(url) ? current : [...current, url]);
  const showPractical = !!(ingredients || allergens.length || storage || shelfLife || product.storeTemp != null);
  const links = [
    ...(descriptions.length ? [["product-about", sv ? "Om produkten" : "About"]] : []),
    ...(kitchen.length ? [["product-kitchen", sv ? "I köket" : "In the kitchen"]] : []),
    ...(nutrients.length || nutrition.length ? [["product-nutrition", sv ? "Näring" : "Nutrition"]] : []),
    ...(showPractical ? [["product-practical", sv ? "Bra att veta" : "Good to know"]] : []),
    ["ProductTrace", sv ? "Spåra din produkt" : "Trace your product"],
  ];

  useEffect(() => { setPhotoIndex(0); }, [language, product.firebaseId]);

  return <main className="ProductDetail ProductProcessedDetail">
    <Seo title={`${name} | VERT.farm`} description={text(product.subtitle) || descriptions[0] || (sv ? "Processad produkt från VERT.farm." : "Processed product from VERT.farm.")} image={heroPhoto || undefined} path={`/farm/products/processed/${product.firebaseId}`} type="product" lang={sv ? "sv" : "en"} />
    <header className="ProductTopbar ProductContainer">
      <Link className="ProductBackLink" to="/farm/products/processed"><ArrowBackIcon />{sv ? "Alla processade produkter" : "All processed products"}</Link>
      <div className="ProductLanguages" role="group" aria-label={sv ? "Språk" : "Language"}>
        <button type="button" aria-pressed={sv} onClick={() => dispatch(setLanguage(0))}>SV</button><span>/</span><button type="button" aria-pressed={!sv} onClick={() => dispatch(setLanguage(1))}>EN</button>
      </div>
    </header>

    <section className="ProductHero ProductContainer" aria-labelledby="product-title">
      <div className="ProductHeroCopy">
        <p className="ProductEyebrow"><span className="ProductLiveDot" />VERT.FARM / {sv ? "PROCESSAT" : "PROCESSED"}</p>
        <h1 id="product-title">{name}</h1>
        {text(product.subtitle) && <p className="ProductHeroIntro">{text(product.subtitle)}</p>}
        <div className="ProductHeroActions">
          <a className="ProductButton" href="#product-contact">{sv ? "Intresserad? Hör av dig" : "Interested? Get in touch"}<ArrowForwardIcon /></a>
          <a className="ProductTextLink" href="#ProductTrace">{sv ? "Spåra din produkt" : "Trace your product"}<span>↘</span></a>
        </div>
        <dl className="ProductHeroFacts">
          <div><dt><SpaOutlinedIcon />{sv ? "Process" : "Method"}</dt><dd>{method}</dd></div>
        </dl>
      </div>
      <div className="ProductGallery">
        <div className="ProductHeroImage">
          {heroPhoto
            ? <img src={heroPhoto} alt={`${name} – ${Math.min(photoIndex + 1, photos.length)}`} fetchpriority="high" width="900" height="1000" onError={() => markFailed(heroPhoto)} />
            : <div className="ProductImagePlaceholder" role="img" aria-label={sv ? "Produktbild saknas" : "Product image unavailable"}><VLogo springAni={{ fill: "#45aa9b" }} /><span>{name}</span></div>}
          {!!photos.length && <span className="ProductPhotoCaption">{sv ? "EN NÄRMARE TITT" : "A CLOSER LOOK"}<span>{String(Math.min(photoIndex + 1, photos.length)).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}</span></span>}
        </div>
        {photos.length > 1 && <div className="ProductThumbnails" role="group" aria-label={sv ? "Produktbilder" : "Product photos"}>
          {photos.map((photo, index) => <button type="button" key={photo} aria-label={`${sv ? "Visa bild" : "Show photo"} ${index + 1}`} aria-pressed={photo === heroPhoto} onClick={() => setPhotoIndex(index)}><img src={photo} alt="" loading="lazy" width="72" height="72" onError={() => markFailed(photo)} /></button>)}
        </div>}
      </div>
    </section>

    {!!links.length && <nav className="ProductSectionNav" aria-label={sv ? "På den här sidan" : "On this page"}><div className="ProductContainer">{links.map(([id, label]) => <a href={`#${id}`} key={id}>{label}</a>)}</div></nav>}

    {!!descriptions.length && <section className="ProductAbout ProductSection ProductContainer" id="product-about">
      <SectionTitle title={sv ? "Från råvara\ntill något nytt." : "Something new\nfrom the familiar."} />
      <div className="ProductStory"><p className="ProductStoryLead">{descriptions[0]}</p>{descriptions[1] && <p>{descriptions[1]}</p>}
        {descriptions.length > 2 && <details className="ProductReadMore"><summary>{sv ? `Mer om ${name}` : `More about ${name}`}<span>+</span></summary>{descriptions.slice(2).map((paragraph, index) => <p key={index}>{paragraph}</p>)}</details>}
      </div>
    </section>}

    {!!profiles.length && <section className="ProductProfile ProductProcessedProfile ProductContainer" aria-label={sv ? "Produktprofil" : "Product profile"}>
      {profiles.map((profile) => <div key={profile.label}><div className="ProductProfileLabel"><span>{profile.label}</span></div><p>{profile.value}</p></div>)}
    </section>}

    {!!kitchen.length && <section className="ProductKitchen ProductSection" id="product-kitchen"><div className="ProductContainer ProductKitchenLayout">
      <div className="ProductKitchenVisual"><SectionTitle title={sv ? "Gott på\nfler sätt." : "More ways\nto enjoy it."} />
        {photos.length > 1 && <figure><img src={photos[photos.length - 1]} alt={name} loading="lazy" width="700" height="650" onError={() => markFailed(photos[photos.length - 1])} /><figcaption><LocalDiningOutlinedIcon />{sv ? "Hitta ditt sätt att använda produkten." : "Find your way to use it."}</figcaption></figure>}
      </div>
      <ol className="ProductKitchenIdeas">{kitchen.map((idea, index) => <li key={index}><span className="ProductIdeaNumber">{String(index + 1).padStart(2, "0")}</span><div><ProductText text={idea} /></div></li>)}</ol>
    </div></section>}

    {(!!nutrients.length || !!nutrition.length) && <section className="ProductNutrition ProductSection ProductContainer" id="product-nutrition">
      <SectionTitle title={sv ? "Lär känna\ninnehållet." : "Get to know\nwhat’s inside."} />
      <div className="ProductProcessedNutritionContent">
        {!!nutrients.length && <ul className="ProductNutrients">{nutrients.map((nutrient, index) => <li key={index}><SpaOutlinedIcon /><div><ProductText text={nutrient} /></div></li>)}</ul>}
        {!!nutrition.length && <table className="ProductNutritionTable"><caption>{sv ? "Näringsvärde per" : "Nutrition per"} {product.nutritionBasis}</caption><tbody>
          {nutrition.map((row, index) => <tr key={`${row.name[0]}-${index}`}><th scope="row">{text(row.name)}</th><td>{new Intl.NumberFormat(sv ? "sv-SE" : "en-GB", { maximumFractionDigits: 3 }).format(row.value)} {row.unit}</td></tr>)}
        </tbody></table>}
      </div>
    </section>}

    {showPractical && <section className="ProductPractical ProductSection ProductContainer" id="product-practical"><SectionTitle title={sv ? "Bra att veta." : "Good to know."} />
      <div className="ProductPracticalGrid ProductProcessedPracticalGrid">
        {ingredients && <div><SpaOutlinedIcon /><h3>{sv ? "Ingredienser" : "Ingredients"}</h3><p>{ingredients}</p></div>}
        {!!allergens.length && <div><InfoOutlinedIcon /><h3>{sv ? "Allergener" : "Allergens"}</h3><ul className="ProductProcessedAllergens">{allergens.map((item, index) => <li key={index}>{item}</li>)}</ul></div>}
        {(storage || shelfLife || product.storeTemp != null) && <div><AcUnitOutlinedIcon /><h3>{sv ? "Förvaring och hållbarhet" : "Storage and shelf life"}</h3>
          {storage && <p>{storage}</p>}
          {product.storeTemp != null && <p>{sv ? "Förvaringstemperatur" : "Storage temperature"}: {product.storeTemp} °C</p>}
          {shelfLife && <p>{sv ? "Hållbarhet" : "Shelf life"}: {shelfLife}</p>}
        </div>}
      </div>
    </section>}

    <FarmProcessedProductTrace product={product} language={language} />
    <section className="ProductContact ProductSection" id="product-contact"><div className="ProductContainer ProductContactInner"><div><h2>{sv ? "Nyfiken på\nprodukten?" : "Curious about\nthis product?"}</h2><p>{sv ? "Hör av dig om du vill veta mer eller prata om en beställning." : "Get in touch to learn more or ask about an order."}</p></div><ContactEnquiry language={language} /></div></section>
    <footer className="ProductFooter ProductContainer"><Link className="ProductWordmark" to="/farm">VERT<span>.farm</span></Link><span>Oskarström, {sv ? "Sverige" : "Sweden"}</span><Link to="/farm/products/processed">{sv ? "Se alla processade produkter" : "See all processed products"}<ArrowForwardIcon /></Link></footer>
  </main>;
}
